/**
 * A tile: a card with a small uppercase title, holding one kind of content —
 * a statement, a valuation, a chart.
 *
 *   <Tile title="Income statement" aside={<TileNote>Q9</TileNote>}>
 *     <StatementRow label="Revenue" value="$2.3M" />
 *     <StatementRow label="Crew" value="$48.2M" indent adornment={<SegmentBar … />} />
 *     <TileMore onClick={…}>+ 8 more cost lines</TileMore>
 *     <StatementRow label="Operating profit" value="-$52.9M" total />
 *   </Tile>
 *
 * Before its content has come, a tile holds a TileEmpty: the empty state,
 * set off from the title as every waiting tile sets it.
 *
 * StatementRow also serves a panel's rows at its smaller size — a label and a
 * figure at the far end, over a hairline — with `size="sm"`.
 *
 * The title also names the tile for assistive technology.
 */

import type { ButtonHTMLAttributes, ReactNode } from 'react';

import { EmptyState } from './empty-state';
import { Figure } from './figure';
import type { Glyph } from './glyphs';
import styles from './tile.module.css';

interface TileProps {
  title: string;
  /** Beside the title, at the far end: a chip, a status. */
  aside?: ReactNode;
  children: ReactNode;
  className?: string;
}

export function Tile({ title, aside, children, className = '' }: TileProps) {
  return (
    <section aria-label={title} className={[styles.tile, className].filter(Boolean).join(' ')}>
      <div className={styles.head}>
        <span>{title}</span>
        {aside}
      </div>
      {children}
    </section>
  );
}

/** A quiet note at the far end of the title — the quarter a statement is for, a chart's range — in the caption ink. */
export function TileNote({ children }: { children: ReactNode }) {
  return <span className={styles.note}>{children}</span>;
}

/** A tile whose content has not come yet: one sentence saying when it will, under a quiet glyph. */
export function TileEmpty({ glyph, children }: { glyph?: Glyph; children: ReactNode }) {
  return (
    <EmptyState glyph={glyph} className={styles.empty}>
      {children}
    </EmptyState>
  );
}

const ROW_SIZE = {
  md: '',
  sm: styles.small,
} as const;

interface StatementRowProps {
  label: ReactNode;
  /** The figure, in the label face with tabular numerals; a string is set as a Figure, a dash in the blank ink. */
  value: ReactNode;
  /** The figure a statement ends on — ending cash, equity — in the firmer weight. */
  strong?: boolean;
  /** A line that belongs to the one above it: set in, in the softer ink. */
  indent?: boolean;
  /** A total: a firmer rule above it. */
  total?: boolean;
  /** Beside the label: a small bar or chip. */
  adornment?: ReactNode;
  /**
   * md for a statement in a tile; sm for the rows of a panel at its 12px —
   * the label in the soft ink, the figure firm, and no hairline under the
   * last row, which the panel's own edge ends.
   */
  size?: keyof typeof ROW_SIZE;
  /** The element: a div, or an li when the rows are a list — the depths of a city, the airlines in it. */
  as?: 'div' | 'li';
}

/** One line of a statement: a label, and its figure at the far end. */
export function StatementRow({
  label,
  value,
  strong = false,
  indent = false,
  total = false,
  adornment,
  size = 'md',
  as: Row = 'div',
}: StatementRowProps) {
  return (
    <Row className={[styles.row, ROW_SIZE[size], indent ? styles.indent : '', total ? styles.total : ''].filter(Boolean).join(' ')}>
      <span className={styles.label}>
        {label}
        {adornment}
      </span>
      <span className={styles.value}>{typeof value === 'string' ? <Figure text={value} strong={strong} /> : value}</span>
    </Row>
  );
}

type TileMoreProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  /**
   * The link that ends the tile — "Show full statement →" — flush with the
   * rows and set off above them. Without it the link sits among the lines,
   * set in like the lines it stands for: "+ 8 more cost lines".
   */
  foot?: boolean;
};

/** A quiet link out of a tile: "+ 8 more cost lines", "Show full statement →". */
export function TileMore({ foot = false, className = '', type = 'button', ...rest }: TileMoreProps) {
  return <button type={type} className={[styles.more, foot ? styles.foot : '', className].filter(Boolean).join(' ')} {...rest} />;
}
